import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import api from '@/lib/api';
import { CheckCircle2, Circle, ArrowRight, Sparkles, Settings, Building2, Users, Link2, Zap } from 'lucide-react';

const stepIcons = [Settings, Building2, Users, Link2, Zap];
const stepDescriptions = [
  'Configure your company name and chase settings',
  'Create a property and set minimum insurance requirements',
  'Import vendors via CSV or add them manually',
  'Generate a magic link. Tell vendors to whitelist noreply.vendorsuite.io',
  'Set up auto-approve rules to streamline reviews',
];

export const OnboardingChecklist = () => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/onboarding/status').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data || data.is_complete) return null;

  return (
    <Card data-testid="onboarding-checklist" className="rounded-xl border border-primary/30 shadow-[var(--shadow-soft)] bg-primary/[0.02]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Sparkles className="h-4 w-4 text-primary" /> Getting Started
          </CardTitle>
          <span className="text-xs text-muted-foreground tabular-nums">{data.completed}/{data.total} complete</span>
        </div>
        <Progress value={data.progress_pct} className="h-1.5 mt-2" />
      </CardHeader>
      <CardContent className="space-y-2">
        {data.steps.map((step, i) => {
          const Icon = stepIcons[i] || Circle;
          return (
            <div key={step.id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors duration-150 cursor-pointer ${step.completed ? 'bg-[hsl(142_56%_97%)] border border-[hsl(142_40%_88%)]' : 'hover:bg-muted/50 border border-transparent'}`}
              onClick={() => !step.completed && navigate(step.link)}
            >
              <div className={`flex h-7 w-7 items-center justify-center rounded-full shrink-0 ${step.completed ? 'bg-[hsl(142,56%,36%)] text-white' : 'bg-muted text-muted-foreground'}`}>
                {step.completed ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${step.completed ? 'line-through text-muted-foreground' : 'font-medium'}`}>{step.title}</p>
                {!step.completed && <p className="text-xs text-muted-foreground mt-0.5">{stepDescriptions[i]}</p>}
              </div>
              {!step.completed && <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />}
            </div>
          );
        })}
        <div className="pt-2">
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => navigate('/guide')}>
            Read the full Quick Start Guide <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
